import React from 'react';

// Data synchronization event system for real-time updates between components
// This system enables background synchronization without affecting the user interface

type DataChangeEvent = {
  type: 'material' | 'metric';
  action: 'created' | 'updated' | 'deleted';
  id: string;
  userId: string;
};

type EventListener = (event: DataChangeEvent) => void;

class DataSyncEventEmitter {
  private listeners: EventListener[] = [];

  // Subscribe to data change events
  subscribe(listener: EventListener): () => void {
    this.listeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.listeners.indexOf(listener);
      if (index > -1) {
        this.listeners.splice(index, 1);
      }
    };
  }

  // Emit data change event
  emit(event: DataChangeEvent): void {
    this.listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('Error in data sync event listener:', error);
      }
    });
  }

  // Emit material change event
  emitMaterialChange(action: 'created' | 'updated' | 'deleted', id: string, userId: string): void {
    this.emit({
      type: 'material',
      action,
      id,
      userId
    });
  }

  // Emit metric change event
  emitMetricChange(action: 'created' | 'updated' | 'deleted', id: string, userId: string): void {
    this.emit({
      type: 'metric',
      action,
      id,
      userId
    });
  }
}

// Create singleton instance
export const dataSyncEvents = new DataSyncEventEmitter();

// React hook for subscribing to data changes
export const useDataSync = (userId: string, onDataChange: () => void) => {
  React.useEffect(() => {
    const unsubscribe = dataSyncEvents.subscribe((event) => {
      // Only trigger refresh if the event is for the current user
      if (event.userId === userId) {
        // Use setTimeout to defer the refresh and avoid blocking the UI
        // Small delay to ensure the current operation completes first
        setTimeout(() => {
          onDataChange();
        }, 100);
      }
    });

    return unsubscribe;
  }, [userId, onDataChange]);
}; 